import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, isDevMode } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { EventBusEvents, NewAlertsEventData, NewMessagesEventData } from '@siza-staff-care/event-bus-constants';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import {
	ApiSessionMsgOrAlertNotification,
	BaseAlertInfoDto,
	MessageDto,
	TypedCollHolder,
	Types,
	UnifiedPollController_Constants,
	UnifiedPollResultDto,
} from '@siza-staff-care/shared/data';
import { NgEventBus } from 'ng-event-bus';
import {
	Observable,
	Subject,
	debounceTime,
	delay,
	exhaustMap,
	filter,
	finalize,
	take,
	takeUntil,
	tap,
	timer,
	withLatestFrom,
} from 'rxjs';
import { AuthStore } from './auth.store';
import { NewMessageCustomSnackbarComponent } from './components/new-message-custom-snackbar.component';
import { CrudService } from './crud.service';
import { NotificationsService } from './notifications';

const Default_Poll_Seconds = 60;

const Use_Notifications = true;
const MessageOrAlert = 18;
export const NotificationSeconds = 8;

interface UnifiedPollerState {
	polling: boolean;
	configured: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class UnifiedPollerStore extends ComponentStore<UnifiedPollerState> {
	private authStore = inject(AuthStore);
	readonly polling$ = this.select(({ polling }) => polling);
	readonly configured$ = this.select(({ configured }) => configured);

	private stopPolling$ = new Subject<void>();

	readonly open = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				// Wait for ready$ to signal true, once.
				this.openOnceAuthReady(
					this.authStore.ready$.pipe(
						filter((ready) => ready),
						take(1),
					),
				);
			}),
		),
	);

	private readonly openOnceAuthReady = this.effect((go$: Observable<boolean>) =>
		go$.pipe(
			debounceTime(100),
			withLatestFrom(this.configured$),
			delay(3000), // Wait 3 seconds before starting to allow other toast messages to clear.
			tap(([ready, configured]) => {
				if (!ready) {
					return;
				}

				if (Use_Notifications) {
					if (!configured) {
						this.setUpNotifications();
						this.patchState({ configured: true });
					}
					return;
				}

				// Note - in development mode, we put a flag on window to determine whether the poll interval is already set up to avoid
				// setting up again and getting multiple polls when reloading after a code change.
				const setUpPoll = !configured && !isDevMode();
				if (!setUpPoll) {
					return;
				}

				const pollSeconds = this.appState.settings.pollSeconds || Default_Poll_Seconds;
				const pollMS = pollSeconds * 1000;

				this.setUpPollTimer(pollMS);
				this.poll(false);
			}),
		),
	);

	readonly openOnceNotTotpSettingUp = this.effect((trigger$) => trigger$.pipe());

	readonly destroy = this.effect((trigger$) => trigger$.pipe());

	readonly handlePollResult = this.effect((result$: Observable<{ dto: UnifiedPollResultDto; causedBy: object }>) =>
		result$.pipe(
			tap((data) => {
				const result = data.dto;
				const causedBy = data.causedBy;

				const allNewMessages = (result.allNewMessages || {}) as TypedCollHolder<MessageDto>,
					allNewAlerts = (result.allNewAlerts || {}) as TypedCollHolder<BaseAlertInfoDto>,
					newMessages = allNewMessages.objects || [],
					newAlerts = allNewAlerts.objects || [];

				const messageCount = result.unreadMessageCount;
				const alertCount = result.unactionedAlertCount;

				this.appState.setMessageCount(messageCount);
				this.appState.setAlertCount(alertCount);

				if (newMessages.length) {
					this.eventBus.cast(EventBusEvents.Event_NewMessages, {
						messages: newMessages,
						causedBy,
					} as NewMessagesEventData);

					this.broadcastMessageRefresh();
					this.showNewMessageSnackbarMessage(newMessages);
				}

				if (newAlerts.length) {
					this.eventBus.cast(EventBusEvents.Event_NewAlerts, {
						alerts: newAlerts,
						causedBy,
					} as NewAlertsEventData);
					this.broadcastAlertRefresh();
				}
			}),
		),
	);

	private readonly showNewMessageSnackbarMessage = this.effect((newMessages$: Observable<MessageDto[]>) =>
		newMessages$.pipe(
			tap((newMessages) => {
				const firstMessage = newMessages[0];
				this.snackbar.openFromComponent(NewMessageCustomSnackbarComponent, {
					data: firstMessage,
					duration: NotificationSeconds * 1000,
					horizontalPosition: 'right',
					verticalPosition: 'bottom',
				});
			}),
		),
	);

	readonly broadcastMessageRefresh = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.eventBus.cast(EventBusEvents.Event_MessageRefresh);
			}),
		),
	);

	readonly broadcastAlertRefresh = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.eventBus.cast(EventBusEvents.Event_AlertRefresh);
			}),
		),
	);

	private readonly setUpNotifications = this.effect((trigger$) =>
		trigger$.pipe(
			exhaustMap(() => {
				// Do a single initial poll to obtain the start state.
				this.poll(true);

				return this.notifications
					.beginSessionNotification<ApiSessionMsgOrAlertNotification>(MessageOrAlert)
					.pipe(
						tap(() => {
							this.poll(true);
						}),
						takeUntil(this.auth.loggedOut$),
						finalize(() => {
							this.patchState({ configured: false });
						}),
					);
			}),
		),
	);

	private readonly poll = this.effect((fromNotification$: Observable<boolean>) =>
		fromNotification$.pipe(
			withLatestFrom(this.polling$),
			tap(([fromNotification, polling]) => {
				if (polling) {
					return;
				}

				if (!fromNotification && this.appState.settings.disablePoll) {
					return;
				}
			}),
			exhaustMap(() => {
				this.patchState({ polling: true });

				return this.crud
					.other<UnifiedPollResultDto>(
						Types.UnifiedPollController,
						UnifiedPollController_Constants.Operation_Poll,
					)
					.pipe(
						tapResponse(
							(result) => {
								this.handlePollResult({ dto: result, causedBy: this });
							},
							(err) => {
								if (err instanceof HttpErrorResponse && err.status === 503) {
									// Server unavailable - stop polling to avoid presenting multiple error dialogs.
									this.stopPolling$.next();
								}
							},
						),
						finalize(() => this.patchState({ polling: false })),
					);
			}),
		),
	);

	readonly setUpPollTimer = this.effect((pollMs$: Observable<number>) =>
		pollMs$.pipe(
			exhaustMap((pollMs) => {
				return timer(pollMs).pipe(
					takeUntil(this.appState.isAuthenticated$),
					takeUntil(this.stopPolling$),
					tap(() => {
						this.poll(false);
					}),
					finalize(() => {
						this.patchState({ configured: false });
					}),
				);
			}),
		),
	);

	readonly triggerPoll = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.poll(true);
			}),
		),
	);

	constructor(
		private appState: AppStateService,
		private crud: CrudService,
		private eventBus: NgEventBus,
		private auth: AuthStore,
		private notifications: NotificationsService,
		private snackbar: MatSnackBar,
	) {
		super({
			polling: false,
			configured: false,
		});
	}
}
